import { FC, useMemo } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, Divider, Stack, Typography } from "@mui/material"
import { useMutationUpdateAttendanceSettings } from "api/reactQuery/mutations/adminTeacher"
import { attendanceSettingsQueryKey } from "api/reactQuery/queries/teacherAdmin"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import * as yup from "yup"

interface AttendanceFormProps {
  defaultValue: number
}

interface FormValues {
  attendance_bonus_amount: number
}

const AttendanceForm: FC<AttendanceFormProps> = ({ defaultValue }) => {
  const { t } = useTranslation("adminDashboard")
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()

  const schema = useMemo(
    () =>
      yup.object({
        attendance_bonus_amount: yup
          .number()
          .typeError(t("bonusAmountRequired"))
          .min(0, t("bonusAmountMin"))
          .required(t("bonusAmountRequired")),
      }),
    [t]
  )

  const methods = useForm<FormValues>({
    defaultValues: { attendance_bonus_amount: defaultValue },
    resolver: yupResolver(schema),
    mode: "onSubmit",
  })

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = methods

  const { mutate, isLoading } = useMutationUpdateAttendanceSettings({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(attendanceSettingsQueryKey)
        showSnackbar({ title: t("attendanceSaved") })
      },
      onError: (error) => {
        if (error.response?.status === 403) {
          showSnackbar({ variant: "error", title: t("notAuthorized") })
        }
      },
    },
  })

  const onSubmit = (values: FormValues) => {
    mutate({ attendance_bonus_amount: String(values.attendance_bonus_amount) })
  }

  return (
    <Box
      sx={{
        maxWidth: 500,
        width: "100%",
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit(onSubmit)()
          }}
        >
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              {t("bonusAmountDescription")}
            </Typography>

            <Divider />

            <TextField
              name="attendance_bonus_amount"
              type="number"
              label={t("bonusAmount")}
              fullWidth
              slotProps={{
                htmlInput: { step: "0.01" },
              }}
              customOnChange={(e, onChange) => {
                const raw = e.target.value
                onChange(raw === "" ? "" : Number(raw))
              }}
            />

            <Button
              type="submit"
              size="large"
              loading={isLoading || isSubmitting}
              disabled={!isDirty}
              fullWidth
            >
              {t("save")}
            </Button>
          </Stack>
        </Box>
      </FormProvider>
    </Box>
  )
}

export default AttendanceForm
